// src/context/CloverContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { onValue } from 'firebase/database';
import * as Sentry from '@sentry/react';
import { dbRef } from 'utils/dbRef';
import config from 'config/clover';
import siteConfig from 'config/site';
import { useContent } from 'context/ContentContext';
import { isStorageBlocked } from 'utils/safeStorage';

export const CloverContext = createContext();

// Error types for better error handling
export const ErrorTypes = {
  NETWORK: 'network',
  DATABASE: 'database',
  TIMEOUT: 'timeout',
  PERMISSION: 'permission',
  RATE_LIMIT: 'rate_limit',
  UNKNOWN: 'unknown',
};

// Cache configuration
const CACHE_KEY = 'clover_menu_cache';
const CACHE_EXPIRY = 60 * 60 * 1000; // 1 hour - show cached menu silently
const RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 1000;

// Logging utility that uses Sentry
const log = (message, ...args) => {
  const logData = args.length > 0 ? { data: args } : undefined;

  if (siteConfig.debug) {
    // Still log to console in development for easy debugging
    // eslint-disable-next-line no-console
    console.log('[CloverContext]', message, ...args);
  }

  // Send to Sentry as a breadcrumb
  Sentry.addBreadcrumb({
    message: `[CloverContext] ${message}`,
    category: 'clover-context-log',
    level: 'info',
    data: logData,
  });
};

// Enhanced error logging with Sentry
const logError = (message, error, context = {}) => {
  // Send to Sentry
  Sentry.captureException(error, {
    tags: {
      component: 'CloverContext',
      ...context.tags,
    },
    extra: {
      message,
      ...context,
    },
  });
};

// Track errors in Sentry with additional context
const trackError = (error, errorType, context = {}) => {
  // A browser that blocks storage fails every cache read and write; that is
  // the visitor's setting, not a fault, so it is not reported (#623).
  if (isStorageBlocked(error)) return;
  Sentry.captureException(error, {
    tags: {
      component: 'CloverContext',
      errorType,
    },
    extra: {
      ...context,
    },
  });
};

export const CloverProvider = ({ children }) => {
  // Get current language from ContentContext
  const { language } = useContent();

  // Menu data state
  const [menuData, setMenuData] = useState({
    items: [],
    categories: [],
    allItems: [],
  });
  const [loadingStates, setLoadingStates] = useState({
    initial: true,
    refresh: false,
    remoteUpdate: false,
  });
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState(null);
  const [useLocalData, setUseLocalData] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [remoteMenuEnabled, setRemoteMenuEnabled] = useState(true);

  // Refs for cleanup
  const mounted = useRef(true);
  const unsubscribeRef = useRef(null);
  const rawSourceUnsubscribeRef = useRef(null);

  // Subscribe to remoteMenuEnabled Firebase flag
  useEffect(() => {
    const flagRef = dbRef('/siteInfo/remoteMenuEnabled');
    const unsubscribeFlag = onValue(flagRef, (snapshot) => {
      if (snapshot.exists()) {
        setRemoteMenuEnabled(Boolean(snapshot.val()));
      }
    });
    return () => unsubscribeFlag();
  }, []);

  // Add Sentry breadcrumb for tracking
  const addBreadcrumb = useCallback((message, data = {}) => {
    Sentry.addBreadcrumb({
      message,
      category: 'clover-context',
      level: 'info',
      data,
    });
  }, []);

  // Cache utilities
  const getCachedData = useCallback(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp > CACHE_EXPIRY) {
        localStorage.removeItem(CACHE_KEY);
        addBreadcrumb('Cache expired', { cacheKey: CACHE_KEY });
        return null;
      }
      addBreadcrumb('Cache hit', { cacheKey: CACHE_KEY });
      return data;
    } catch (err) {
      log('Cache read error:', err);
      trackError(err, 'cache_read', { operation: 'getCachedData' });
      return null;
    }
  }, [addBreadcrumb]);

  const setCachedData = useCallback(
    (data) => {
      try {
        localStorage.setItem(
          CACHE_KEY,
          JSON.stringify({
            data,
            timestamp: Date.now(),
          }),
        );
        addBreadcrumb('Cache updated', { cacheKey: CACHE_KEY });
      } catch (err) {
        log('Cache write error:', err);
        trackError(err, 'cache_write', { operation: 'setCachedData' });
      }
    },
    [addBreadcrumb],
  );

  // Error handling utilities
  const determineErrorType = useCallback((error) => {
    const errorMessage = error?.message || '';
    const errorCode = error?.code || '';

    if (
      errorCode === 'PERMISSION_DENIED' ||
      errorMessage.includes('permission_denied')
    ) {
      return ErrorTypes.PERMISSION;
    }

    if (errorCode === 'RATE_LIMITED' || errorMessage.includes('rate limit')) {
      return ErrorTypes.RATE_LIMIT;
    }

    if (errorMessage.includes('timeout') || error.name === 'AbortError') {
      return ErrorTypes.TIMEOUT;
    }

    if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
      return ErrorTypes.NETWORK;
    }

    if (errorMessage.includes('database')) {
      return ErrorTypes.DATABASE;
    }

    return ErrorTypes.UNKNOWN;
  }, []);

  const getErrorMessage = useCallback(() => {
    switch (errorType) {
      case ErrorTypes.NETWORK:
        return 'Network connection issue. Please check your internet connection.';
      case ErrorTypes.TIMEOUT:
        return 'Request timed out. The server is taking too long to respond.';
      case ErrorTypes.DATABASE:
        return 'There was an issue accessing the menu data from our database.';
      case ErrorTypes.PERMISSION:
        return 'You do not have permission to access this data.';
      case ErrorTypes.RATE_LIMIT:
        return 'Too many requests. Please try again later.';
      default:
        return 'An unexpected error occurred loading the menu.';
    }
  }, [errorType]);

  // No local fallback data - show error message instead
  const loadLocalData = useCallback(async () => {
    addBreadcrumb('No local fallback data available');
    log('Menu data unavailable - Firebase failed and no fallback exists');
    return false;
  }, [addBreadcrumb]);

  // Fetch menu data with retry logic
  const fetchMenuData = useCallback(
    async (showLoading = true, retryCount = 0) => {
      if (!mounted.current) return false;

      if (showLoading) {
        setLoadingStates((prev) => ({ ...prev, refresh: true }));
      }

      setError(null);
      setErrorType(null);

      try {
        addBreadcrumb('Fetching menu data', { retryCount, showLoading });

        // Set a timeout for the Firebase request
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout

        // Use raw menu for admin, language-specific menu for frontend
        // Admin needs raw source data for toggles/availability
        const isAdmin =
          typeof window !== 'undefined' &&
          window.location.pathname.startsWith('/admin');
        const menuPath = isAdmin
          ? `/menus/latest`
          : `/menus/${language}/latest`;
        const menuRef = dbRef(menuPath);

        // Force fetch from server to avoid stale cache
        let snapshot = await new Promise((resolve, reject) => {
          let unsubscribe;
          unsubscribe = onValue(
            menuRef,
            (snap) => {
              if (unsubscribe) unsubscribe();
              resolve(snap);
            },
            (error) => {
              if (unsubscribe) unsubscribe();
              reject(error);
            },
            { onlyOnce: true },
          );
        });

        clearTimeout(timeoutId);

        // If language-specific menu doesn't exist, fallback to English
        if (!snapshot.exists() && language !== 'en') {
          log(`${language} menu not found, falling back to English`);
          addBreadcrumb('Falling back to English menu', {
            requestedLanguage: language,
          });
          const enMenuRef = dbRef('/menus/en/latest');
          snapshot = await new Promise((resolve, reject) => {
            let unsubscribe;
            unsubscribe = onValue(
              enMenuRef,
              (snap) => {
                if (unsubscribe) unsubscribe();
                resolve(snap);
              },
              (error) => {
                if (unsubscribe) unsubscribe();
                reject(error);
              },
              { onlyOnce: true },
            );
          });
        }

        if (!snapshot.exists()) {
          throw new Error('Menu data not found in database');
        }

        let firebaseData = snapshot.val();

        if (
          !firebaseData ||
          typeof firebaseData !== 'object' ||
          !Array.isArray(firebaseData.categories)
        ) {
          throw new Error('Invalid menu structure: missing categories array');
        }

        // For frontend (non-admin), merge data from multiple sources
        if (!isAdmin) {
          try {
            // Fetch modifier translations for current language
            const modifierGroupsRef = dbRef(
              `/menus/${language}/modifierGroups`,
            );
            const modifierGroupsSnapshot = await new Promise(
              (resolve, reject) => {
                let unsubscribe;
                unsubscribe = onValue(
                  modifierGroupsRef,
                  (snap) => {
                    if (unsubscribe) unsubscribe();
                    resolve(snap);
                  },
                  (error) => {
                    if (unsubscribe) unsubscribe();
                    reject(error);
                  },
                  { onlyOnce: true },
                );
              },
            );

            const modifierTranslations = modifierGroupsSnapshot.exists()
              ? modifierGroupsSnapshot.val()
              : {};

            // Fetch raw source data (prices, toggles, structure)
            const rawSourceRef = dbRef('/menus/latest');
            const rawSnapshot = await new Promise((resolve, reject) => {
              let unsubscribe;
              unsubscribe = onValue(
                rawSourceRef,
                (snap) => {
                  if (unsubscribe) unsubscribe();
                  resolve(snap);
                },
                (error) => {
                  if (unsubscribe) unsubscribe();
                  reject(error);
                },
                { onlyOnce: true },
              );
            });

            if (rawSnapshot.exists()) {
              const rawData = rawSnapshot.val();
              // Merge: language menu (names/descriptions) + modifier translations + raw source (prices/toggles)
              firebaseData.categories = firebaseData.categories
                .map((cat) => {
                  // Skip null/undefined categories
                  if (!cat || !cat.id) return null;

                  const rawCat = rawData.categories?.find(
                    (c) => c?.id === cat.id,
                  );
                  return {
                    ...cat,
                    // Use toggles from raw source (operational data)
                    showOnWeb:
                      rawCat?.showOnWeb !== undefined ? rawCat.showOnWeb : true,
                    isSoldOnline:
                      rawCat?.isSoldOnline !== undefined
                        ? rawCat.isSoldOnline
                        : true,
                    // Use raw source item order (preserves admin-configured ordering)
                    items: Array.isArray(rawCat?.items)
                      ? rawCat.items.map((rawItem) => {
                          const translatedItem = cat.items?.find(
                            (i) => i?.id === rawItem?.id,
                          );

                          // Build modifier groups from raw source + translations
                          const mergedModifierGroups = (
                            rawItem?.modifierGroups || []
                          ).map((rawGroup) => {
                            // Look up translated names from global modifier groups
                            const groupTranslation =
                              modifierTranslations[rawGroup.id];

                            return {
                              id: rawGroup.id,
                              name: groupTranslation?.name || rawGroup.name, // Translated name or fallback
                              maxAllowed: rawGroup.maxAllowed,
                              minRequired: rawGroup.minRequired,
                              modifiers: (rawGroup.modifiers || []).map(
                                (rawMod) => {
                                  // Look up translated modifier name
                                  const modTranslation =
                                    groupTranslation?.modifiers?.[rawMod.id];

                                  return {
                                    id: rawMod.id,
                                    name: modTranslation?.name || rawMod.name, // Translated name or fallback
                                    price: rawMod.price,
                                    available: rawMod.available,
                                  };
                                },
                              ),
                            };
                          });

                          return {
                            id: rawItem.id,
                            name: translatedItem?.name || rawItem.name, // From language menu or fallback to raw
                            description:
                              translatedItem?.description ||
                              rawItem.description ||
                              '', // From language menu or fallback
                            price: rawItem?.price || 0, // From raw source
                            imageUrl: rawItem?.imageUrl || '', // From raw source
                            modifierGroups: mergedModifierGroups,
                            // Use toggles from raw source
                            showOnWeb:
                              rawItem?.showOnWeb !== undefined
                                ? rawItem.showOnWeb
                                : true,
                            isSoldOnline:
                              rawItem?.isSoldOnline !== undefined
                                ? rawItem.isSoldOnline
                                : true,
                            available:
                              rawItem?.available !== undefined
                                ? rawItem.available
                                : true,
                          };
                        })
                      : [],
                  };
                })
                .filter((cat) => cat !== null); // Remove any null categories
            }
          } catch (err) {
            logError('Error merging menu data from multiple sources', err);
            // Continue with language menu data if merge fails
          }
        }

        // Flatten items from categories
        const flattenedItems = firebaseData.categories.flatMap((cat) =>
          Array.isArray(cat.items) ? cat.items : [],
        );

        firebaseData.items = flattenedItems;

        if (mounted.current) {
          log('Firebase menu data loaded successfully');
          addBreadcrumb('Menu data loaded', { source: 'firebase' });
          setMenuData(firebaseData);
          setCachedData(firebaseData);
          setUseLocalData(false);
          setLastUpdated(new Date(firebaseData.updatedAt || Date.now()));
        }

        return true;
      } catch (err) {
        logError('Error fetching menu from Firebase', err, {
          operation: 'fetchMenuData',
          retryCount,
          source: 'firebase',
        });

        if (!mounted.current) return false;

        const type = determineErrorType(err);

        // Retry logic
        if (retryCount < RETRY_ATTEMPTS && type === ErrorTypes.NETWORK) {
          log(`Retrying fetch (attempt ${retryCount + 1}/${RETRY_ATTEMPTS})`);
          addBreadcrumb('Retrying menu fetch', {
            attempt: retryCount + 1,
            maxAttempts: RETRY_ATTEMPTS,
          });
          await new Promise((resolve) =>
            setTimeout(resolve, RETRY_DELAY * Math.pow(2, retryCount)),
          );
          return fetchMenuData(showLoading, retryCount + 1);
        }

        // Track the error in Sentry
        trackError(err, type, {
          operation: 'fetchMenuData',
          retryCount,
          source: 'firebase',
        });

        // Fall back to cached or local data
        const cachedData = getCachedData();
        if (cachedData) {
          log('Using cached data - Firebase unavailable but cache is valid');
          setMenuData(cachedData);
          // Clear error since we have valid cached data
          setError(null);
          setErrorType(null);
          return true;
        }

        // Only show error if cache is expired/missing
        log('No valid cache available - showing error');
        setError(err.message);
        setErrorType(type);

        if (config.app.useLocalDataFallback) {
          log('Calling load local data');
          return loadLocalData();
        }

        return false;
      } finally {
        if (mounted.current && showLoading) {
          setLoadingStates((prev) => ({ ...prev, refresh: false }));
        }
      }
    },
    [
      getCachedData,
      setCachedData,
      loadLocalData,
      determineErrorType,
      addBreadcrumb,
      language,
    ],
  );

  // React to toggles of remoteMenuEnabled: re-fetch or load local
  useEffect(() => {
    if (remoteMenuEnabled) {
      // when re-enabled, fetch fresh data
      fetchMenuData(true);
    } else {
      // when disabled, switch to local fallback
      loadLocalData();
    }
  }, [remoteMenuEnabled, fetchMenuData, loadLocalData]);

  // Refetch menu when language changes
  useEffect(() => {
    if (remoteMenuEnabled && language) {
      log(`Language changed to ${language}, fetching menu...`);
      fetchMenuData(true);
    }
  }, [language, remoteMenuEnabled, fetchMenuData]);

  // Initialize Firebase connection
  useEffect(() => {
    mounted.current = true;

    if (!remoteMenuEnabled) {
      log('Remote menu data is disabled');
      loadLocalData();
      setLoadingStates((prev) => ({ ...prev, initial: false }));
      return;
    }

    const initializeFirebase = async () => {
      try {
        addBreadcrumb('Initializing Firebase connection');

        // Check cache first
        const cachedData = getCachedData();
        if (cachedData) {
          setMenuData(cachedData);
          setLoadingStates((prev) => ({ ...prev, initial: false }));
        }

        // Fetch fresh data
        await fetchMenuData(!cachedData);

        // Set up real-time listener for menu
        // Admin needs raw source data for toggles/availability
        const isAdmin =
          typeof window !== 'undefined' &&
          window.location.pathname.startsWith('/admin');
        const menuPath = isAdmin
          ? `/menus/latest`
          : `/menus/${language}/latest`;
        const menuRef = dbRef(menuPath);

        // For frontend, also listen to /menus/latest for toggle changes
        if (!isAdmin) {
          const rawSourceRef = dbRef('/menus/latest');
          rawSourceUnsubscribeRef.current = onValue(rawSourceRef, () => {
            // When toggles change, re-fetch to trigger merge
            if (mounted.current) {
              log('Toggle update detected in /menus/latest, re-fetching...');
              fetchMenuData(false);
            }
          });
        }

        unsubscribeRef.current = onValue(
          menuRef,
          (snapshot) => {
            if (!mounted.current) return;

            if (snapshot.exists()) {
              log('Real-time menu update received');
              addBreadcrumb('Real-time update received', {
                source: 'firebase',
              });
              // Re-fetch to trigger merge logic
              fetchMenuData(false);
            }
          },
          (err) => {
            if (!mounted.current) return;
            logError('Firebase listener error', err, {
              operation: 'firebase_listener',
              realtime: true,
            });
            const type = determineErrorType(err);
            trackError(err, type, {
              operation: 'firebase_listener',
              realtime: true,
            });

            // Check if we have valid cached data before showing error
            const cachedData = getCachedData();
            if (cachedData) {
              log('Firebase listener error but using valid cached data');
              setError(null);
              setErrorType(null);
            } else {
              log('Firebase listener error and no valid cache - showing error');
              setError(err.message);
              setErrorType(type);
            }
          },
        );
      } catch (error) {
        if (mounted.current) {
          logError('Firebase initialization error', error, {
            operation: 'firebase_init',
          });
          const type = determineErrorType(error);
          trackError(error, type, {
            operation: 'firebase_init',
          });
          setError(error.message);
          setErrorType(type);
        }
      } finally {
        if (mounted.current) {
          setLoadingStates((prev) => ({ ...prev, initial: false }));
        }
      }
    };

    initializeFirebase();

    return () => {
      mounted.current = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      if (rawSourceUnsubscribeRef.current) {
        rawSourceUnsubscribeRef.current();
      }
    };
  }, [
    getCachedData,
    setCachedData,
    fetchMenuData,
    loadLocalData,
    determineErrorType,
    addBreadcrumb,
    remoteMenuEnabled,
    language,
  ]);

  // Function to refresh the menu data
  const refreshMenu = useCallback(async () => {
    addBreadcrumb('Manual menu refresh triggered');
    return fetchMenuData(true);
  }, [fetchMenuData, addBreadcrumb]);

  // Trigger a manual refresh via Netlify function
  const triggerRemoteRefresh = useCallback(async () => {
    if (!mounted.current) return false;

    let refreshUrl; // Declare refreshUrl outside the try block

    try {
      setLoadingStates((prev) => ({ ...prev, remoteUpdate: true }));
      addBreadcrumb('Triggering remote menu refresh');

      refreshUrl = config.clover.refreshUrl;

      const response = await fetch(refreshUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-token': import.meta.env.VITE_ADMIN_TOKEN || '',
        },
        mode: 'cors',
        credentials: 'same-origin',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error ${response.status}: ${errorText}`);
      }

      addBreadcrumb('Remote refresh successful');

      // Wait for Firebase to update, then fetch new data
      await new Promise((resolve) => setTimeout(resolve, 2000));
      await fetchMenuData(false);

      return true;
    } catch (err) {
      logError('Error triggering remote menu refresh', err, {
        operation: 'remote_refresh',
        url: refreshUrl,
      });
      if (mounted.current) {
        const type = determineErrorType(err);
        trackError(err, type, {
          operation: 'remote_refresh',
          url: refreshUrl,
        });
        setError(`Failed to trigger menu refresh: ${err.message}`);
        setErrorType(type);
      }
      return false;
    } finally {
      if (mounted.current) {
        setLoadingStates((prev) => ({ ...prev, remoteUpdate: false }));
      }
    }
  }, [fetchMenuData, determineErrorType, addBreadcrumb]);

  // Memoized context value
  const value = useMemo(
    () => ({
      // Menu data state
      menuData:
        menuData && typeof menuData === 'object'
          ? {
              items: menuData.items || [],
              categories: menuData.categories || [],
              allItems: menuData.allItems || [],
            }
          : { items: [], categories: [], allItems: [] },
      loading:
        loadingStates.initial ||
        loadingStates.refresh ||
        loadingStates.remoteUpdate,
      loadingStates,
      error,
      errorType,
      useLocalData,
      lastUpdated,
      errorMessage: getErrorMessage(),
      hasMenuData:
        (Array.isArray(menuData?.items) && menuData.items.length > 0) ||
        (Array.isArray(menuData?.categories) &&
          menuData.categories.some(
            (cat) => cat && Array.isArray(cat.items) && cat.items.length > 0,
          )),

      // Functions
      refreshMenu,
      triggerRemoteRefresh,
      setMenuData,

      remoteMenuEnabled,
    }),
    [
      menuData,
      loadingStates,
      error,
      errorType,
      useLocalData,
      lastUpdated,
      getErrorMessage,
      refreshMenu,
      triggerRemoteRefresh,
      remoteMenuEnabled,
    ],
  );

  return (
    <CloverContext.Provider value={value}>{children}</CloverContext.Provider>
  );
};

// PropTypes definitions
CloverProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useCloverContext = () => {
  const context = useContext(CloverContext);
  if (context === undefined) {
    throw new Error('useCloverContext must be used within a CloverProvider');
  }
  return context;
};
